// Flujos de análisis de marca — webhooks n8n `generar_identidad` y
// `crear_logo` migrados a atlas `/v1/brand/identity` y `/v1/brand/logo`
// (ADR-598). Flag ADENTRO del helper, patrón de siempre:
//   ON  → atlas con JWT (`user_id` del body NO viaja, sale del token).
//   OFF → webhook n8n byte-idéntico (token estático + user_id en el body).
//
// Mejoras del atlas:
//  • identity devuelve el análisis REAL en `{response: "<markdown>"}` — n8n
//    respondía sin ese campo y el caller guardaba '' en
//    config.media_agent_analysis. El update del caller queda igual; ahora
//    persiste el valor correcto. Timeout esperable: 2-3 min en la rama web.
//  • logo responde 402 tipado sin créditos (n8n se colgaba sin responder).

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';

const GENERAR_IDENTIDAD_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_identidad';
const CREAR_LOGO_URL = 'https://ai-agency-dt7d.onrender.com/webhook/crear_logo';

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

export interface BrandIdentityBody {
  user_id: string;
  /** El atlas solo acepta 'create' (y es opcional); las ramas chat/parse del
   *  webhook viejo están muertas también en n8n (ver MarketingPlanChat). */
  mode: string;
  logo_url?: string;
  is_default_brand?: boolean;
  nombre_brand?: string;
  rubro_brand?: string;
  description_brand?: string;
  target_public_brand?: string;
  website_url?: string;
}

/**
 * Análisis de identidad de marca. Devuelve el `Response` CRUDO (el caller de
 * BrandDescription maneja status/parse él mismo — corre en background con su
 * propio lock y marca `webhookError` según el tipo de fallo).
 */
export async function postBrandIdentity(
  body: BrandIdentityBody,
): Promise<Response> {
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...atlasBody } = body;
    return atlasBackendFetch('/v1/brand/identity', atlasBody);
  }
  return fetch(GENERAR_IDENTIDAD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
}

export interface BrandLogoBody {
  user_id: string;
  is_default_brand?: boolean;
  brand_name?: string;
  brand_rubro?: string;
  brand_description?: string;
  brand_target_public?: string;
  /** Se manda igual que a n8n; ambos backs lo ignoran. */
  website_url?: string;
}

/** Genera el logo IA. Devuelve `logo_url`; sin créditos tira el copy de saldo. */
export async function postBrandLogo(body: BrandLogoBody): Promise<string> {
  let response: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...atlasBody } = body;
    // Etiqueta para el panel: viaja en el body y el backend la persiste en
    // datos_generacion (scrub del req.body). Solo en el path atlas — el body
    // a n8n no se toca.
    response = await atlasBackendFetch('/v1/brand/logo', {
      ...atlasBody,
      modo_utilizado: 'crear_logo',
    });
    if (response.status === 402) throw new Error(BILLING_MESSAGE);
  } else {
    response = await fetch(CREAR_LOGO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
  }
  if (!response.ok) throw new Error('Error al generar el logo');
  const data = (await response.json().catch(() => null)) as {
    logo_url?: string;
  } | null;
  if (!data?.logo_url) throw new Error('No se recibió la URL del logo');
  return data.logo_url;
}
